import { useState } from "react";

// Mood ratings over time (1-10), one series: a 2px line in the theme colour,
// 8px points, a quiet grid, and a tooltip on hover/focus. The dated list
// beside it on the page is the table view.
export default function MoodChart({ points, height = 160 }) {
  const [hover, setHover] = useState(null);
  if (!points.length) return null;
  const W = 640, H = height, L = 28, R = 12, T = 10, B = 22;
  const days = points.map((p) => new Date(`${p.day}T00:00:00`).getTime());
  const min = Math.min(...days), max = Math.max(...days);
  const x = (t) => (max === min ? L + (W - L - R) / 2 : L + ((t - min) / (max - min)) * (W - L - R));
  const y = (m) => T + ((10 - m) / 9) * (H - T - B);
  const path = points.map((p, i) => `${i ? "L" : "M"}${x(days[i]).toFixed(1)},${y(p.mood).toFixed(1)}`).join(" ");
  const fmt = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label={`Mood ratings, ${points.length} days, latest ${points[points.length - 1].mood} out of 10`}>
        {[1, 4, 7, 10].map((v) => (
          <g key={v}>
            <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="currentColor" className="text-secondary-200" strokeWidth="1" />
            <text x={L - 6} y={y(v) + 3} textAnchor="end" className="fill-secondary-500" fontSize="10">{v}</text>
          </g>
        ))}
        <text x={L} y={H - 6} className="fill-secondary-500" fontSize="10">{fmt(points[0].day)}</text>
        {points.length > 1 && <text x={W - R} y={H - 6} textAnchor="end" className="fill-secondary-500" fontSize="10">{fmt(points[points.length - 1].day)}</text>}
        <path d={path} fill="none" stroke="currentColor" className="text-primary-600" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => (
          <g key={p.day} tabIndex={0} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} onFocus={() => setHover(i)} onBlur={() => setHover(null)} style={{ outline: "none" }}>
            <circle cx={x(days[i])} cy={y(p.mood)} r="12" fill="transparent" />
            <circle cx={x(days[i])} cy={y(p.mood)} r={hover === i ? 5 : 4} fill="currentColor" className={p.mood <= 2 ? "text-rose-600" : "text-primary-600"} stroke="white" strokeWidth="2" />
          </g>
        ))}
      </svg>
      {hover !== null && (
        <div className="pointer-events-none absolute -translate-x-1/2 -translate-y-full rounded-lg bg-secondary-900 px-2.5 py-1.5 text-xs text-white shadow"
          style={{ left: `${(x(days[hover]) / W) * 100}%`, top: `${(y(points[hover].mood) / H) * 100}%`, marginTop: -8 }}>
          <span className="font-semibold">{points[hover].mood}/10</span> · {fmt(points[hover].day)}
          {points[hover].note && <span className="block max-w-[200px] truncate text-secondary-300">{points[hover].note}</span>}
        </div>
      )}
    </div>
  );
}
